import { useEffect, useMemo, useState } from 'react';
import { Typography } from '@material-ui/core';
import cx from 'classnames';
import { map } from 'lodash';

import { BouncingDots } from 'src/components/BouncingDots';
import { PersonaSelector } from 'src/components/PersonaSelector';
import { StaticPanel } from 'src/components/StaticPanel';
import { UpdatePersonaBrandVoiceNotification } from 'src/components/UpdatePersonaBrandVoiceNotification';
import { isSameLanguage } from 'src/data/generation';
import { isToolExcludedFromSuggestedPersonas } from 'src/data/generationTool';
import { CUSTOMER_PERSONA_EMPTY_LABEL } from 'src/data/persona';
import { STONLY_ID } from 'src/lib/stonly';
import { useBrandVoices, useVisiblePersonas } from 'src/services';
import { useBrandVoicesAssets, useCustomerPersonaActions } from 'src/store';
import { PersonaSelectionPopup } from 'src/views/PersonaSelectionPopup';
import { useUpdateAssetBrandVoicePopup } from 'src/views/UpdateAssetBrandVoicePopup';

import { useFallbackToDefaultCustomerPersona, useFetchSuggestedPersonas } from '../../services';

import { useDisplayLoadingFromURL } from './useDisplayLoadingFromURL';
import { useHandlers } from './useHandlers';
import { useIsDisabled } from './useIsDisabled';
import { useSetHasHiddenSuggestedPersonas } from './useSetHasHiddenSuggestedPersonas';
import { useStyles } from './useStyles';
import { useViewState } from './useViewState';

const VISIBLE_COUNT = 3;

export const BlockPersonas = ({
  className,
  titleBold,
  alwaysEnabled = false,
  disabledCollapsed,
  visibleCount = VISIBLE_COUNT,
  generationTool,
  language,
}) => {
  const styles = useStyles();
  const { brandVoices, defaultBrandVoice } = useBrandVoices();
  const brandVoiceByLanguage = useMemo(() => {
    if (isSameLanguage(defaultBrandVoice?.language, language)) {
      return defaultBrandVoice;
    }
    return brandVoices.find((brandVoice) => isSameLanguage(brandVoice.language, language));
  }, [language, defaultBrandVoice, brandVoices]);

  useFetchSuggestedPersonas();
  useFallbackToDefaultCustomerPersona();

  const {
    selectedCustomerPersona,
    hasHiddenSuggestedPersonas,
    suggestedCustomerPersonas,
    loadingSuggestedCustomerPersonas,
    isManagePersonasPopupOpen,
  } = useViewState();
  const displayLoadingFromURL = useDisplayLoadingFromURL();
  const disabled = useIsDisabled();

  const { targetAudiences, brandVoicesAssets, loadingBrandVoiceAssets } = useBrandVoicesAssets();

  const [savedPersona, setSavedPersona] = useState(null);
  const [displayNotification, setDisplayNotification] = useState(false);
  const openUpdateAssetBrandVoicePopup = useUpdateAssetBrandVoicePopup();
  const savedPersonaBrandVoice = brandVoicesAssets?.find(
    (bv) => bv.id === savedPersona?.brandVoice?.id
  );

  const onSave = (persona) => {
    setDisplayNotification(true);
    setSavedPersona(persona);
    setTimeout(() => {
      setDisplayNotification(false);
    }, 10000);
  };

  const {
    handleOpenManagePersonasPopup,
    handleCloseManagePersonasPopup,
    handleSelectPersona,
    handleEditPersona,
    handleDeletePersona,
    handlePersonaSeen,
    handleSavePersona,
  } = useHandlers(brandVoiceByLanguage, onSave, language);

  const targetAudiencesForLanguage = useMemo(() => {
    return targetAudiences.filter((persona) =>
      isSameLanguage(persona.brandVoice?.language, language)
    );
  }, [targetAudiences, language]);

  useEffect(() => {
    if (
      selectedCustomerPersona?.isSuggested ||
      map(targetAudiencesForLanguage, 'id').includes(selectedCustomerPersona?.id)
    ) {
      return;
    }
    handleSelectPersona(null);
  }, [handleSelectPersona, selectedCustomerPersona, targetAudiencesForLanguage]);

  const loading =
    loadingBrandVoiceAssets || displayLoadingFromURL || loadingSuggestedCustomerPersonas;

  const visiblePersonas = useVisiblePersonas({
    loading,
    personas: targetAudiencesForLanguage,
    suggestedPersonas: suggestedCustomerPersonas,
    selectedPersona: selectedCustomerPersona,
    visibleCount,
    language,
  });

  const customerPersonaActions = useCustomerPersonaActions();

  useSetHasHiddenSuggestedPersonas(visiblePersonas);

  const personaCount = visibleCount + 2;
  const hasNonDisplayedPersonas =
    targetAudiencesForLanguage.length + suggestedCustomerPersonas.length > visibleCount;

  return (
    <StaticPanel
      className={cx(styles.root, className)}
      classes={{ title: styles.title }}
      title="Target audience"
      titleStonlyId={!disabledCollapsed ? STONLY_ID.targetAudience : null}
      titleBold={titleBold}
      data-testid="BlockPersonas"
    >
      {/* TODO: Consider using `useSelectPersonaPopup` instead */}
      <PersonaSelectionPopup
        open={isManagePersonasPopupOpen}
        onClose={handleCloseManagePersonasPopup}
        onSubmit={handleCloseManagePersonasPopup}
        selectedPersona={selectedCustomerPersona}
        displaySuggested={!isToolExcludedFromSuggestedPersonas(generationTool)}
        brandVoice={defaultBrandVoice}
        language={language}
        isEditor
      />
      {displayLoadingFromURL && !disabledCollapsed && (
        <div className={styles.loadingRow}>
          <Typography className={styles.loadingFromUrl}>
            Analyzing your URL for new audiences
          </Typography>
          <BouncingDots classes={{ root: styles.dots, dot: styles.singleDot }} />
        </div>
      )}
      {disabledCollapsed && (
        <div className={styles.collapsedPersona}>
          {selectedCustomerPersona?.name || CUSTOMER_PERSONA_EMPTY_LABEL}
        </div>
      )}
      {!disabledCollapsed && (
        <PersonaSelector
          className={styles.selector}
          selectedPersonaId={selectedCustomerPersona?.id}
          onSelect={handleSelectPersona}
          onEdit={handleEditPersona}
          onDelete={handleDeletePersona}
          onShowMore={handleOpenManagePersonasPopup}
          onSave={handleSavePersona}
          disabled={disabledCollapsed || (!alwaysEnabled && disabled)}
          isLoading={loading}
          onSeen={handlePersonaSeen}
          hasHiddenSuggestedPersonas={hasHiddenSuggestedPersonas}
          visiblePersonas={visiblePersonas}
          reducedHeight={false}
          personaCount={personaCount}
          showMoreButton={[brandVoiceByLanguage]?.length > 0}
          brandVoices={[brandVoiceByLanguage]}
          hasNonDisplayedPersonas={hasNonDisplayedPersonas}
          isSaving={customerPersonaActions.isSavingCustomerPersona()}
        />
      )}
      {displayNotification && savedPersona && (
        <UpdatePersonaBrandVoiceNotification
          onClick={() => {
            openUpdateAssetBrandVoicePopup({
              asset: savedPersona,
              language: savedPersonaBrandVoice?.language || savedPersona?.brandVoice?.language,
              onSave,
            });
            setDisplayNotification(false);
            setSavedPersona(null);
          }}
          savedPersona={savedPersona}
        />
      )}
    </StaticPanel>
  );
};
